import { useEffect, useState, useCallback } from "react";
import PropiedadMapaUbicacion from "../propiedades/PropiedadMapaUbicacion";
import AddressAutocompleteInput from "../AddressAutocompleteInput";
import api from "../../services/api";
import { coordsEnMarinaAlta, CENTRO_DENIA } from "../../utils/geoMarinaAlta";
import { PLACES_LOCALITY_RESTRICTION_RADIUS_M } from "../../services/googlePlacesAutocomplete";

const DEFAULT_RADIO_M = 300;
const RADIO_MIN = 50;
const RADIO_MAX = 15000;

function parseCoord(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function normPobl(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

async function fetchCentroPoblacion(poblacion) {
  const pob = String(poblacion || "").trim();
  if (!pob) return null;
  if (normPobl(pob) === "denia") {
    return { ...CENTRO_DENIA, source: "denia" };
  }
  try {
    const r = await api.get("/geo/localidad-centro", {
      params: { poblacion: pob, provincia: "Alicante" },
    });
    if (r.data?.ok && coordsEnMarinaAlta(r.data.lat, r.data.lng)) {
      return { lat: r.data.lat, lng: r.data.lng, source: r.data.source || "api" };
    }
  } catch {
    /* fallback abajo */
  }
  return null;
}

/**
 * Visor de mapa para definir centro + radio de búsqueda en la demanda del cliente.
 */
export default function ModalDemandaZonaMapa({
  open,
  onClose,
  poblacion,
  geoLat,
  geoLng,
  geoRadioM,
  onApply,
  theme,
}) {
  const c = theme?.colors || {};
  const isDark = theme?.name === "dark";

  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [radioM, setRadioM] = useState(DEFAULT_RADIO_M);
  const [centroCargando, setCentroCargando] = useState(false);
  const [centroErr, setCentroErr] = useState("");
  const [localityCenter, setLocalityCenter] = useState(null);
  const [calleBuscar, setCalleBuscar] = useState("");

  const initModal = useCallback(async () => {
    const pob = String(poblacion || "").trim();
    if (!pob) {
      setCentroErr("Selecciona una población en los criterios antes de usar el mapa.");
      setLocalityCenter(null);
      setLat(null);
      setLng(null);
      return;
    }
    setCentroCargando(true);
    setCentroErr("");
    const center = await fetchCentroPoblacion(pob);
    if (!center) {
      setCentroErr(`No se pudo centrar el mapa en «${pob}». Haz clic en el mapa o busca una calle.`);
      setLocalityCenter(null);
      setLat(null);
      setLng(null);
      setCentroCargando(false);
      return;
    }
    setLocalityCenter(center);

    const savedLat = parseCoord(geoLat);
    const savedLng = parseCoord(geoLng);
    if (savedLat != null && savedLng != null && coordsEnMarinaAlta(savedLat, savedLng)) {
      setLat(savedLat);
      setLng(savedLng);
    } else {
      if (savedLat != null && savedLng != null && !coordsEnMarinaAlta(savedLat, savedLng)) {
        setCentroErr("La ubicación guardada no era válida; se ha recentrado en la población.");
      }
      setLat(center.lat);
      setLng(center.lng);
    }
    setCentroCargando(false);
  }, [poblacion, geoLat, geoLng]);

  useEffect(() => {
    if (!open) return;
    setCalleBuscar("");
    setRadioM(
      parseCoord(geoRadioM) && Number(geoRadioM) >= RADIO_MIN
        ? Math.round(Number(geoRadioM))
        : DEFAULT_RADIO_M,
    );
    initModal();
  }, [open, geoRadioM, initModal]);

  if (!open) return null;

  const radioKm = (radioM / 1000).toFixed(radioM >= 1000 ? 1 : 2);
  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    fontSize: 12,
    borderRadius: 8,
    border: `1px solid ${c.border || "#e2e8f0"}`,
    background: c.bgInput || (isDark ? "#0e1117" : "#fff"),
    color: c.textPrimary || "#0f172a",
    boxSizing: "border-box",
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-zona-mapa-titulo"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 12,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: c.bgCard || "#fff",
          borderRadius: 12,
          border: `1px solid ${c.border || "#e2e8f0"}`,
          boxShadow: "0 16px 48px rgba(0,0,0,0.25)",
          overflow: "visible",
        }}
      >
        <div
          style={{
            padding: "12px 14px",
            borderBottom: `1px solid ${c.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div>
            <h3
              id="modal-zona-mapa-titulo"
              style={{ margin: 0, fontSize: 14, fontWeight: 700, color: c.textPrimary }}
            >
              Zona en mapa
            </h3>
            <p style={{ margin: "2px 0 0", fontSize: 11, color: c.textMuted }}>
              {poblacion ? `Población: ${poblacion}` : "Sin población seleccionada"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              lineHeight: 1,
              cursor: "pointer",
              color: c.textMuted,
            }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div style={{ padding: "10px 14px 12px" }}>
          <label
            style={{
              display: "block",
              fontSize: 11,
              fontWeight: 600,
              color: c.textPrimary,
              marginBottom: 4,
            }}
          >
            Buscar calle
          </label>
          <AddressAutocompleteInput
            value={calleBuscar}
            onValueChange={setCalleBuscar}
            placeholder={poblacion ? `Escribe una calle en ${poblacion}…` : "Escribe una calle…"}
            style={inputStyle}
            placesLocalityContext={
              localityCenter
                ? {
                    latitude: localityCenter.lat,
                    longitude: localityCenter.lng,
                    radiusM: PLACES_LOCALITY_RESTRICTION_RADIUS_M,
                  }
                : null
            }
            onPlaceAddressResolved={(parts) => {
              const la = parseCoord(parts?.latitud);
              const ln = parseCoord(parts?.longitud);
              if (la != null && ln != null && coordsEnMarinaAlta(la, ln)) {
                setLat(la);
                setLng(ln);
                const linea = [parts.calle, parts.numero].filter(Boolean).join(" ").trim();
                if (linea) setCalleBuscar(linea);
                setCentroErr("");
              } else if (la != null && ln != null) {
                setCentroErr("Esa dirección queda fuera de la zona habitual del CRM.");
              }
            }}
          />

          {centroErr ? (
            <p style={{ margin: "8px 0 0", fontSize: 11, color: "#dc2626" }}>{centroErr}</p>
          ) : null}
          {centroCargando ? (
            <p style={{ margin: "8px 0 0", fontSize: 11, color: c.textMuted }}>Centrando mapa…</p>
          ) : null}

          {!centroCargando && lat != null && lng != null ? (
            <div style={{ marginTop: 10 }}>
              <PropiedadMapaUbicacion
                key={`zona-mapa-${lat.toFixed(5)}-${lng.toFixed(5)}-${radioM}`}
                lat={lat}
                lng={lng}
                radiusM={radioM}
                mapMode="satellite"
                cityView
                onCoordsChange={(la, ln) => {
                  if (coordsEnMarinaAlta(la, ln)) {
                    setLat(la);
                    setLng(ln);
                    setCentroErr("");
                  }
                }}
                fallbackCenter={{ lat, lng }}
                active
                isDark={isDark}
                borderColor={c.border}
                mapHeight={220}
              />
            </div>
          ) : null}

          <div style={{ marginTop: 10 }}>
            <label
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                fontWeight: 600,
                color: c.textPrimary,
                marginBottom: 4,
              }}
            >
              <span>Radio de búsqueda</span>
              <span style={{ color: "#4f46e5" }}>{radioKm} km</span>
            </label>
            <input
              type="range"
              min={RADIO_MIN}
              max={RADIO_MAX}
              step={50}
              value={radioM}
              onChange={(e) => setRadioM(Number(e.target.value))}
              style={{ width: "100%" }}
            />
            <p style={{ margin: "6px 0 0", fontSize: 10, color: c.textMuted, lineHeight: 1.35 }}>
              Con población marcada: entran todas las de esa población; el radio acota solo las que tienen
              GPS. Para un punto concreto, reduce el radio y busca la calle arriba.
            </p>
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              disabled={lat == null || lng == null}
              onClick={() => onApply({ geoLat: lat, geoLng: lng, geoRadioM: radioM })}
              style={{
                flex: 1,
                minWidth: 100,
                padding: "8px 12px",
                borderRadius: 8,
                border: "none",
                background: "#4f46e5",
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
                cursor: lat == null ? "not-allowed" : "pointer",
                opacity: lat == null ? 0.5 : 1,
              }}
            >
              Aplicar
            </button>
            <button
              type="button"
              onClick={() => initModal()}
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                background: "transparent",
                color: c.textPrimary,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Centrar población
            </button>
            <button
              type="button"
              onClick={() => onApply({ geoLat: null, geoLng: null, geoRadioM: null })}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                background: "transparent",
                color: c.textMuted,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Quitar
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                background: "transparent",
                color: c.textPrimary,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
